import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Calendar as CalendarIcon, MapPin, Package, Users, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinDrop = () => {
  const { brand } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [drop, setDrop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    items: [{ category: 'mallet', name: '', quantity: 1 }],
    solo_estimate: '',
    notes: '',
    terms_accepted: false
  });

  // Map brand slug to drop ID
  const dropIdMap = {
    'casablanca': 'casablanca',
    'emita': 'emita'
  };

  useEffect(() => {
    const dropId = dropIdMap[brand];
    if (!dropId) {
      toast.error('Invalid drop');
      navigate('/calendar');
      return;
    }
    fetchDrop(dropId);
  }, [brand]);

  useEffect(() => {
    // Pre-fill form with user data if authenticated
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || ''
      }));
    }
  }, [user]);

  const fetchDrop = async (dropId) => {
    try {
      const response = await axios.get(`${API}/drops/${dropId}`);
      setDrop(response.data);
    } catch (error) {
      console.error('Failed to fetch drop', error);
      toast.error('Failed to load drop details');
    } finally {
      setLoading(false);
    }
  };

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options = { day: 'numeric', month: 'short' };
    const yearOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    return `${start.toLocaleDateString('en-GB', options)} to ${end.toLocaleDateString('en-GB', yearOptions)}`;
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { category: 'other', name: '', quantity: 1 }]
    }));
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = field === 'quantity' ? Number(value) : value;
    setFormData(prev => ({ ...prev, items: updatedItems }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const updatedItems = formData.items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, items: updatedItems }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.full_name || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    
    if (!formData.items.some(item => item.name.trim())) {
      toast.error('Please add at least one item');
      return;
    }
    
    if (!formData.terms_accepted) {
      toast.error('You must accept the Terms & Conditions to join a drop');
      return;
    }

    setSubmitting(true);
    
    try {
      const payload = {
        drop_id: dropIdMap[brand],
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        items: formData.items.filter(item => item.name.trim()),
        solo_estimate: Number(formData.solo_estimate) || 0,
        notes: formData.notes,
        terms_accepted: formData.terms_accepted
      };

      await axios.post(`${API}/join-drop`, payload);
      
      toast.success('Submission successful! Check your email for confirmation.');
      navigate('/calendar');
    } catch (error) {
      console.error('Submission failed', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5EFE6] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!drop) {
    return (
      <div className="min-h-screen bg-[#F5EFE6] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 text-lg">Drop not found</p>
          <Link to="/calendar" className="text-primary mt-4 inline-block">Return to Calendar</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE6]" data-testid="join-drop-page">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-2xl font-playfair font-bold text-primary tracking-tight">
            Sharedequal
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/calendar" className="text-sm font-medium text-primary">
              Calendar
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-sm">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-sm">
                  Login
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Back Button */}
        <Link to="/calendar" className="inline-flex items-center gap-2 text-gray-600 hover:text-primary mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Calendar</span>
        </Link>

        {/* Drop Info Card */}
        <Card className="bg-white border border-gray-200 rounded-xl shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="text-2xl font-playfair">{drop.brand}</CardTitle>
            <CardDescription>{drop.name}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <CalendarIcon className="w-4 h-4 text-gray-400" />
                <span>{formatDateRange(drop.start_date, drop.end_date)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span>{drop.location || 'Singapore'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Package className="w-4 h-4 text-gray-400" />
                <span>Collection: {drop.collection_info || 'TBD'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="w-4 h-4 text-gray-400" />
                <span>Participants: {drop.participant_count || 'TBD'}</span>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                <strong>Service Fee:</strong> SGD {drop.service_fee_standard} (standard) / SGD {drop.service_fee_oversize} (oversize)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Join Form */}
        <Card className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <CardHeader>
            <CardTitle className="text-xl">Express Your Interest</CardTitle>
            <CardDescription>
              This is an initial interest form. Submit your basic details and we'll coordinate the specifics with you directly.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Expectation Note */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> This form collects your basic interest. Specific product details (sizes, customizations, exact items) will be confirmed by our team via WhatsApp or email after submission.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                    placeholder="Your full name"
                    required
                    data-testid="input-full-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="phone">Phone / WhatsApp *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+65 9XXX XXXX"
                  required
                  data-testid="input-phone"
                />
                <p className="text-xs text-gray-500 mt-1">We'll use this to coordinate order details with you</p>
              </div>

              {/* Items - Simplified */}
              <div>
                <Label className="mb-2 block">What are you interested in? *</Label>
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Select
                        value={item.category}
                        onValueChange={(value) => updateItem(index, 'category', value)}
                      >
                        <SelectTrigger className="col-span-4" data-testid={`select-category-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mallet">Mallet</SelectItem>
                          <SelectItem value="boots">Boots</SelectItem>
                          <SelectItem value="helmet">Helmet</SelectItem>
                          <SelectItem value="gloves">Gloves</SelectItem>
                          <SelectItem value="apparel">Apparel</SelectItem>
                          <SelectItem value="equipment">Equipment</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Brief description (optional)"
                        value={item.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                        className="col-span-5"
                        data-testid={`input-item-name-${index}`}
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="col-span-2"
                        min="1"
                        data-testid={`input-item-qty-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="col-span-1 text-red-500 hover:text-red-700 hover:bg-red-50"
                        disabled={formData.items.length === 1}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={addItem} 
                  className="mt-3"
                  data-testid="add-item-button"
                >
                  + Add Item
                </Button>
              </div>

              {/* Solo Estimate */}
              <div>
                <Label htmlFor="solo_estimate">Solo Shipping Estimate (SGD)</Label>
                <Input
                  id="solo_estimate"
                  type="number"
                  value={formData.solo_estimate}
                  onChange={(e) => setFormData(prev => ({ ...prev, solo_estimate: e.target.value }))}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  data-testid="input-solo-estimate"
                />
                <p className="text-xs text-gray-500 mt-1">Your estimated solo shipping cost (used for solo cap protection)</p>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any preferences, questions, or specific items you're looking for..."
                  rows={3}
                  data-testid="input-notes"
                />
              </div>

              {/* Terms Acceptance */}
              <div className="flex items-start space-x-3 p-4 bg-stone-50 border border-stone-200 rounded-lg">
                <Checkbox
                  id="terms-accepted"
                  data-testid="terms-accepted-checkbox"
                  checked={formData.terms_accepted}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, terms_accepted: checked }))}
                  className="mt-0.5"
                />
                <label htmlFor="terms-accepted" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
                  I confirm I have read and agree to the{' '}
                  <Link to="/terms-and-conditions" className="text-primary hover:underline font-medium" target="_blank">
                    Terms & Conditions
                  </Link>
                </label>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg py-3 font-medium"
                disabled={submitting || !formData.terms_accepted}
                data-testid="submit-join-drop"
              >
                {submitting ? 'Submitting...' : 'Submit Interest'}
              </Button>

              {/* Post-Submit Note */}
              <p className="text-xs text-center text-gray-500 mt-4">
                After submission, our team will contact you via WhatsApp or email to confirm specific product details, sizes, and customizations. No payment required at this stage.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JoinDrop;
